<script setup>
import OnsiteOptimizerChangesTableHeader from "@/components/admin/onsiteOptimiser/table/OnsiteOptimizerChangesTableHeader.vue";
import OnsiteOptimiserHeadChanges from "@/components/admin/onsiteOptimiser/table/OnsiteOptimiserHeadChanges.vue";
import OnsiteOptimiserElementStates from "@/components/admin/onsiteOptimiser/table/OnsiteOptimiserElementStates.vue";
import CustomMultiselect from "@/components/global-themed/CustomMultiselect.vue";
import {onMounted, ref, watch} from "vue";
import axios from "axios";
import {axiosCatchHandler} from "../../../modules/sharedGlobal";
import SweetToast from "../../../SweetToast";
import Swal from "sweetalert2";

const props = defineProps({
  website: {
    type: Object,
    required: true,
  },
});

const loading = ref(false);
const pageState = ref(null);
const pages = ref(null);
const selectedPage = ref(null);

const dismissChange = async (elementState) => {
  loading.value = true;

  try {
    await axios.delete(`/ajax/website/${props.website.id}/onsite-optimizer/page/${selectedPage.value}/dismiss-change/${elementState.uuid}`);
  } catch (error) {
    axiosCatchHandler(error, () => {});
    loading.value = false;

    SweetToast.fire({
      icon: 'error',
      title: error.response.data.message,
    });
  }

  await fetchPages();
  await fetchPageState();

  loading.value = false;
}

const dismissHeadChange = async (headChange) => {
  loading.value = true;

  try {
    await axios.delete(`/ajax/website/${props.website.id}/onsite-optimizer/page/${selectedPage.value}/dismiss-head-change/${headChange.name}`);
  } catch (error) {
    axiosCatchHandler(error, () => {});
    loading.value = false;

    SweetToast.fire({
      icon: 'error',
      title: error.response.data.message,
    });
  }

  await fetchPages();
  await fetchPageState();

  loading.value = false;
}

const revertToOriginal = async () => {
  loading.value = true;

  try {
    await axios.delete(`/ajax/website/${props.website.id}/onsite-optimizer/page/${selectedPage.value}/revert-to-original`);
  } catch (error) {
    axiosCatchHandler(error, () => {});
    loading.value = false;

    SweetToast.fire({
      icon: 'error',
      title: error.response.data.message,
    });
  }

  await fetchPages();
  await fetchPageState();

  loading.value = false;
}

const deleteWebsiteState = async () => {
  Swal.fire({
    icon: 'warning',
    title: 'Delete all website changes?',
    html: `
        You are about to delete all saved and published changes for the entire website.
        <br><br> This cannot be undone.
        <br><br> Are you sure you wish to proceed?
    `,
    showCancelButton: true,
    confirmButtonText: 'Delete',
    confirmButtonColor: '#d33',
    showLoaderOnConfirm: true,
    backdrop: true,
    preConfirm: () => {
      return axios.delete(`/ajax/website/${props.website.id}/onsite-optimizer/website-state`)
        .then(() => {
          Swal.fire({
            icon: 'success',
            title: 'All website changes deleted successfully',
            didClose: () => {
              location.reload();
            }
          });
        }).catch(() => {
          Swal.fire({
            icon: 'error',
            title: 'Whoops',
            html: 'Something went wrong deleting all website changes'
          });
        })
    }
  })

}

const fetchPageState = async () => {
  const {data} = await axios.get(`/ajax/website/${props.website.id}/onsite-optimizer/page/${selectedPage.value}/state`);
  pageState.value = data.pageState;
}

const fetchPages = async () => {
  const {data} = await axios.get(`/ajax/website/${props.website.id}/onsite-optimizer/pages`);
  pages.value = data.pages;

  if (pages.value.length < 1) {
    selectedPage.value = null;
  }
}

watch(selectedPage, async () => {
  loading.value = true;

  await fetchPageState();

  loading.value = false;
});

onMounted(async () => {
  loading.value = true;

  await fetchPages();

  if (pages.value.length > 0) {
    selectedPage.value = pages.value[0].value;
  }

  loading.value = false;
});
</script>

<template>
  <div class="tw-flex tw-flex-col tw-mb-12">
    <div class="tw-flex tw-justify-between tw-items-center">
      <h1 class="tw-text-4.5xl tw-text-font-primary tw-font-bold tw-font-sans">
        Onsite Optimizer - Manage Changes
      </h1>
      <button
        class="
            tw-flex tw-flex-row tw-items-center tw-justify-center tw-gap-2.5 tw-font-bold tw-transition-colors
            disabled:tw-cursor-not-allowed disabled:tw-text-slate-300
            tw-h-10 tw-rounded-lg tw-text-denali tw-leading-normal tw-border-denali tw-border tw-shadow-denali tw-whitespace-nowrap tw-p-3
            hover:tw-text-denali-dark hover:tw-border-denali-dark hover:tw-shadow-denali-dark
        "
        :disabled="loading"
        @click="deleteWebsiteState"
      >
        Delete Website Changes
        <svg-vue
          icon="icons/trash"
          class="tw-w-4 tw-h-4"
        />
      </button>
    </div>
    <p class="tw-text-font-secondary tw-text-base">
      Manage Onsite Optimizer changes
    </p>
  </div>

  <div class="tw-bg-white tw-rounded-lg tw-p-8 tw-flex tw-flex-col tw-space-y-8 tw-flex-1 tw-overflow-hidden">
    <div class="tw-flex tw-justify-between">
      <div class="tw-w-1/2">
        <CustomMultiselect
          id="selectedPage"
          v-model="selectedPage"
          class="tw-text-left"
          :close-on-select="true"
          :options="pages"
          :searchable="true"
          :can-deselect="false"
        />
      </div>

      <div class="tw-flex">
        <button
          class="
            tw-flex tw-flex-row tw-items-center tw-justify-center tw-gap-2.5 tw-font-bold tw-transition-colors
            disabled:tw-cursor-not-allowed disabled:tw-text-slate-300
            tw-h-10 tw-rounded-lg tw-text-blue tw-leading-normal tw-border-blue tw-border tw-shadow-blue tw-whitespace-nowrap tw-p-3"
          :disabled="loading || !selectedPage"
          @click="revertToOriginal"
        >
          Revert to original
          <svg-vue
            icon="icons/trash"
            class="tw-w-4 tw-h-4"
          />
        </button>
      </div>
    </div>

    <OnsiteOptimizerChangesTableHeader />

    <template v-if="loading">
      <div
        class="tw-flex tw-flex-col tw-items-center tw-space-y-10 tw-justify-center tw-my-24 tw-w-full tw-max-w-sm tw-mx-auto"
      >
        <svg-vue
          icon="icons/loading"
          class="tw-w-1/2 tw-h-auto tw-text-blue/20"
        />
        <p class="tw-text-2xl tw-text-center">
          Loading
        </p>
      </div>
    </template>


    <div
      v-else-if="pageState && (pageState.elementStates.length > 0 || pageState.headChanges.length > 0)"
    >
      <div
        class="tw-rounded-xl tw-border tw-border-grey-light tw-text-grey-dark"
      >
        <OnsiteOptimiserHeadChanges
          :head-changes="pageState.headChanges"
          @dismiss="headChange => dismissHeadChange(headChange)"
        />

        <OnsiteOptimiserElementStates
          :element-states="pageState.elementStates"
          @dismiss="elementState => dismissChange(elementState)"
        />
      </div>
    </div>

    <div
      v-else
      class="tw-flex tw-flex-col tw-items-center tw-space-y-10 tw-justify-center tw-my-24 tw-w-full tw-max-w-sm tw-mx-auto tw-h-[30vh]"
    >
      <svg-vue
        icon="illustrations/landscape-blue"
        class="tw-w-56"
      />

      <p class="tw-text-2xl tw-text-center tw-whitespace-nowrap">
        Looks like there are no changes here.
      </p>
    </div>
  </div>
</template>
